import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import type { UIErrorMessage } from './ui-error-message-builder';
import { operationsText } from '../../app/shared/utils/http-texts';

export type Method = keyof typeof operationsText;
export type ApiContexts = 'arrecadacao' | 'unknown' | 'auth' | 'medico' | 'pessoa' | 'prestador' | 'comissoes';

export abstract class UiErrorParser {
    method = '';
    target = '';
    messageStart = '';
    type: ApiContexts = 'unknown';
    ignore = false;
    title?: string;
    severity = 'error';
    private methodTexts = operationsText;

    abstract verifyContext(error: HttpErrorResponse, req: HttpRequest<unknown>): boolean;
    abstract errorComplement(error: HttpErrorResponse, req: HttpRequest<unknown>): string;
    abstract beforeTextBuild(error: HttpErrorResponse, req: HttpRequest<unknown>): void;

    parseError(error: HttpErrorResponse, req: HttpRequest<unknown>): UIErrorMessage {
        const method = req.method;
        this.setMethod(this.selectMethodText(method as Method));
        this.beforeTextBuild(error, req);

        return {
            type: this.type,
            method: this.method,
            url: req.url,
            text: this.buildText(error, req),
            ignore: this.ignore,
            title: this.title ?? '',
            severity: this.severity
        };
    }

    private selectMethodText(method: Method) {
        return this.methodTexts[method];
    }

    buildWithOperation(): string {
        return this.method.replace('{{target}}', this.target);
    }

    buildText(error: HttpErrorResponse, req: HttpRequest<unknown>) {
        const complement = this.errorComplement(error, req);
        let message = `${this.messageStart} ${this.buildWithOperation()}`;
        if (complement && complement.length > 0) {
            message = message + `\n${complement}`;
        }
        return message;
    }

    setMethod(method: string) {
        this.method = method;
    }
    setTarget(target: string) {
        this.target = target;
    }
    setType(type: ApiContexts) {
        this.type = type;
    }
    setMessageStart(start: string) {
        this.messageStart = start;
    }
    setIgnore(ignore: boolean) {
        this.ignore = ignore;
    }

    setTitle(title: string) {
        this.title = title;
    }

    setSeverity(severity: string) {
        this.severity = severity;
    }

    protected extractErrorMessage(error: HttpErrorResponse): string {
        const raw = error?.error;
        let payload: Record<string, unknown> | null = null;

        if (raw && typeof raw === 'object') {
            payload = raw as Record<string, unknown>;
        } else if (typeof raw === 'string' && raw.trim()) {
            try {
                const parsed = JSON.parse(raw);
                payload = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : { detail: raw };
            } catch {
                payload = { detail: raw };
            }
        }

        return (
            this.normalizeMessage(payload?.['detail']) ||
            this.normalizeMessage(payload?.['message']) ||
            // CfmErrorDto pode trazer a mensagem só no `title` (regra de negócio 4xx com `errors`
            // nulo, caso em que o handler serializa `detail` como "[]"). Sem esta terceira opção
            // o erro chegaria vazio na tela.
            this.normalizeMessage(payload?.['title']) ||
            ''
        );
    }

    private normalizeMessage(value: unknown): string {
        if (value == null) {
            return '';
        }

        if (Array.isArray(value)) {
            const texts = value.map((item) => this.normalizeMessage(item)).filter(Boolean);
            return texts.join('\n');
        }

        const text = String(value).trim();
        if (!text) {
            return '';
        }

        // `CustomExceptionHandler` grava `detail` como `List#toString()` do Java, então a mensagem
        // chega embrulhada em colchetes ("[]" quando não há itens). Desembrulha para não exibir a
        // sintaxe da lista ao usuário — e devolve vazio quando a lista estava vazia, permitindo
        // que a busca siga para `message`/`title`.
        if (text.startsWith('[') && text.endsWith(']')) {
            const inner = text.slice(1, -1).trim();
            return inner.replace(/^["']+|["']+$/g, '').trim();
        }

        return text;
    }
}
